import { getRefreshToken, registerSessionCleanup } from "@shared/lib/auth"
import {
	isAppActive,
	onAppActiveChange,
	socialBaseUrl,
} from "@shared/lib/platform"
import { createStore } from "@tanstack/store"
import posthog from "posthog-js"
import { API_BASE } from "../constants"
import { getValidAccessToken } from "../http"
import { createConnection, type Snapshot } from "./connection"

function getUrl(): string {
	let base = window.location.origin
	let prefix = socialBaseUrl
	if (socialBaseUrl.startsWith("http")) {
		base = socialBaseUrl
		prefix = ""
	}
	const url = new URL(base)
	let wsProtocol = "ws:"
	if (url.protocol === "https:") wsProtocol = "wss:"

	return `${wsProtocol}//${url.host}${prefix}${API_BASE}`
}

async function getToken(): Promise<string | undefined> {
	const token = await getValidAccessToken()
	if (token) {
		return token
	}
	if (await getRefreshToken()) {
		throw new Error("Token refresh unavailable")
	}
	return undefined
}

function onOnlineChange(handler: (isOnline: boolean) => void): () => void {
	const ctrl = new AbortController()
	window.addEventListener("online", () => handler(true), {
		signal: ctrl.signal,
	})
	window.addEventListener("offline", () => handler(false), {
		signal: ctrl.signal,
	})
	return () => ctrl.abort()
}

const connection = createConnection({
	createSocket: (token) =>
		new WebSocket(`${getUrl()}/ws?token=${encodeURIComponent(token)}`),
	getToken,
	isAppActive,
	onAppActiveChange,
	isOnline: () => navigator.onLine,
	onOnlineChange,
	capture: (event, properties) => posthog.capture(event, properties),
	captureException: (error, properties) =>
		posthog.captureException(error, properties),
})

const store = createStore<Snapshot>(connection.snapshot())

connection.on("message", (frame) => {
	import("./contract")
		.then(({ checkFrame }) => checkFrame(frame))
		.catch(() => {})
})

connection.on("state", (snapshot) => {
	store.setState(() => snapshot)
})

export const wsStore = Object.assign(store, {
	connect: connection.connect,
	disconnect: connection.disconnect,
	send: connection.send,
	on: connection.on,
})

export function wsUserId(): number | null {
	const { userId } = store.state
	if (!userId) return null
	return Number(userId)
}

export function waitForConnection(timeoutMs: number): Promise<boolean> {
	if (store.state.isConnected) return Promise.resolve(true)

	return new Promise((resolve) => {
		let timer: ReturnType<typeof setTimeout> | null = null
		const off = connection.on("state", (snapshot) => {
			if (!snapshot.isConnected) return
			if (timer !== null) clearTimeout(timer)
			off()
			resolve(true)
		})
		timer = setTimeout(() => {
			off()
			resolve(false)
		}, timeoutMs)
	})
}

registerSessionCleanup(() => connection.disconnect())
